import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CATEGORY_LABELS } from '@kon/shared';
import { adminApi } from '@/lib/api';
import { queryKeys } from '@/lib/queryClient';
import { isoDateNDaysAgo, money, todayIso } from '@/lib/format';
import { Icon } from '@/components/ui/Icon';
import ui from '@/components/ui/shared.module.css';
import styles from './Dashboard.module.css';

const RANGES = [
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
];

export function Dashboard() {
  const [days, setDays] = useState(29);
  const query = useMemo(() => ({ from: isoDateNDaysAgo(days), to: todayIso() }), [days]);
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.stats(query),
    queryFn: ({ signal }) => adminApi.getStats(query, signal),
  });

  const maxRevenue = useMemo(
    () => Math.max(1, ...(data?.revenueSeries.map((p) => p.revenueCents) ?? [0])),
    [data],
  );

  return (
    <>
      <div className={ui.pageHead}>
        <div>
          <h1 className={ui.pageTitle}>Dashboard</h1>
          <p className={ui.pageSub}>Revenue and order performance</p>
        </div>
        <div className={styles.rangeBar} role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button
              key={r.label}
              className={`${styles.rangeBtn} ${days === r.days ? styles.rangeActive : ''}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isError ? (
        <div className={ui.empty}>
          <span className={ui.emptyGlyph}>⚠️</span>
          Couldn't load dashboard stats.
        </div>
      ) : (
        <>
          <div className={ui.statGrid}>
            <StatCard label="Revenue" value={data ? money(data.revenueCents) : '—'} accent loading={isLoading} />
            <StatCard label="Paid orders" value={data ? String(data.paidOrderCount) : '—'} loading={isLoading} />
            <StatCard
              label="Avg. order value"
              value={data ? money(data.averageOrderValueCents) : '—'}
              loading={isLoading}
            />
            <StatCard
              label="Pending payment"
              value={data ? String(data.pendingOrderCount) : '—'}
              gold
              loading={isLoading}
            />
          </div>

          <div className={ui.sectionGrid}>
            {/* Revenue chart */}
            <div className={ui.panel}>
              <div className={ui.panelHead}>Revenue over time</div>
              <div className={ui.panelBody}>
                {data && data.revenueSeries.length > 0 ? (
                  <>
                    <div className={styles.chart}>
                      {data.revenueSeries.map((point) => (
                        <div
                          key={point.date}
                          className={styles.bar}
                          style={{ height: `${(point.revenueCents / maxRevenue) * 100}%` }}
                          title={`${point.date}: ${money(point.revenueCents)} · ${point.orderCount} orders`}
                        />
                      ))}
                    </div>
                    <div className={styles.chartAxis}>
                      <span>{data.range.from}</span>
                      <span>Peak {money(maxRevenue)}</span>
                      <span>{data.range.to}</span>
                    </div>
                  </>
                ) : (
                  <div className={styles.chartEmpty}>No revenue in this range yet.</div>
                )}
              </div>
            </div>

            {/* Low stock */}
            <div className={ui.panel}>
              <div className={ui.panelHead}>Low stock</div>
              <div className={ui.panelBody}>
                {data && data.lowStock.length > 0 ? (
                  <div className={styles.list}>
                    {data.lowStock.slice(0, 8).map((item) => (
                      <div key={item.productId} className={styles.listItem}>
                        <div className={styles.itemMain}>
                          <div className={styles.itemName}>{item.name}</div>
                          <div className={styles.itemSub}>≤ {item.threshold} threshold</div>
                        </div>
                        <span className={styles.lowStock}>{item.stock} left</span>
                      </div>
                    ))}
                    <Link to="/inventory" style={{ marginTop: 12, color: 'var(--color-accent)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
                      Manage inventory →
                    </Link>
                  </div>
                ) : (
                  <div className={styles.miniEmpty}>All products are well stocked. 🎉</div>
                )}
              </div>
            </div>
          </div>

          {/* Top sellers */}
          <div className={ui.panel} style={{ marginTop: 'var(--space-5)' }}>
            <div className={ui.panelHead}>Top sellers</div>
            <div className={ui.panelBody}>
              {data && data.topSellers.length > 0 ? (
                <div className={styles.list}>
                  {data.topSellers.map((seller, i) => (
                    <div key={seller.productId} className={styles.listItem}>
                      <span className={styles.rank}>{i + 1}</span>
                      <div className={styles.itemMain}>
                        <div className={styles.itemName}>{seller.name}</div>
                        <div className={styles.itemSub}>
                          {seller.category ? CATEGORY_LABELS[seller.category] : 'Deleted product'} ·{' '}
                          {seller.unitsSold} sold
                        </div>
                      </div>
                      <span className={styles.itemValue}>{money(seller.revenueCents)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.miniEmpty}>No sales in this range yet.</div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  accent,
  gold,
  loading,
}: {
  label: string;
  value: string;
  accent?: boolean;
  gold?: boolean;
  loading?: boolean;
}) {
  return (
    <div className={ui.statCard}>
      <span className={ui.statLabel}>
        <Icon name={gold ? 'receipt' : 'dashboard'} size={15} />
        {label}
      </span>
      <div className={`${ui.statValue} ${accent ? ui.statAccent : ''} ${gold ? ui.statGold : ''}`}>
        {loading ? '···' : value}
      </div>
    </div>
  );
}
